/**
 * <puffy-statement> — parallax band.
 *
 * The backdrop translates against the scroll while the section crosses the
 * viewport. The listener is culled the moment the section leaves the screen:
 * a parallax that keeps computing off screen is pure jank on a mid-range
 * Android arriving from a TikTok click.
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class PuffyStatement extends HTMLElement {
  #layer = null;
  #strength = 0;
  #scrollHandler = null;
  #observer = null;
  #rafId = 0;
  #ticking = false;
  #active = false;

  connectedCallback() {
    this.#layer = this.querySelector('[data-parallax-layer]');
    this.#strength = (Number(this.dataset.parallax) || 0) / 100;

    if (!this.#layer || this.#strength === 0) return;
    if (window.matchMedia(REDUCED_MOTION).matches) return;

    this.#scrollHandler = () => this.#queue();

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          const onScreen = entries.some((entry) => entry.isIntersecting);
          if (onScreen === this.#active) return;
          this.#active = onScreen;
          if (onScreen) {
            document.addEventListener('scroll', this.#scrollHandler, { capture: true, passive: true });
            this.#queue();
          } else {
            document.removeEventListener('scroll', this.#scrollHandler, { capture: true });
          }
        },
        { rootMargin: '120px 0px' }
      );
      this.#observer.observe(this);
    } else {
      this.#active = true;
      document.addEventListener('scroll', this.#scrollHandler, { capture: true, passive: true });
      this.#queue();
    }
  }

  disconnectedCallback() {
    if (this.#scrollHandler) {
      document.removeEventListener('scroll', this.#scrollHandler, { capture: true });
    }
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#rafId);
  }

  #queue() {
    if (this.#ticking) return;
    this.#ticking = true;
    this.#rafId = requestAnimationFrame(() => this.#update());
  }

  #update() {
    this.#ticking = false;

    const rect = this.getBoundingClientRect();
    const span = innerHeight + rect.height;
    if (span <= 0) return;

    // -1 when the section is just below the fold, +1 when it has just left the
    // top. The backdrop is 14 percent taller than the section at both ends, so
    // the travel can never expose an edge.
    const progress = 1 - ((rect.bottom / span) * 2);
    const shift = progress * this.#strength * rect.height * 0.14;

    this.#layer.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0)`;
  }
}

if (!customElements.get('puffy-statement')) {
  customElements.define('puffy-statement', PuffyStatement);
}
